"use client";

import { useLocale } from "next-intl";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface Step {
  label: string;
  index: number;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  containerClassName?: string;
  indicatorWrapperClassName?: string;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  containerClassName,
  indicatorWrapperClassName,
}) => {
  const locale = useLocale();
  const isRTL = locale === "ar";

  const uniqueLabels = useMemo(() => Array.from(new Set(steps.map((step) => step.label))), [steps]);
  const circleRefs = useMemo(() => uniqueLabels.map(() => React.createRef<HTMLDivElement>()), [uniqueLabels.length]);

  const containerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);

  const [lineDimensions, setLineDimensions] = useState({
    progressWidth: 0,
    totalWidth: 0,
    lineLeft: 0,
    progressLeft: 0,
  });

  const calculateDimensions = () => {
    if (circleRefs.some((ref) => !ref.current) || !innerContainerRef.current) return;

    const containerRect = innerContainerRef.current.getBoundingClientRect();

    // Get actual visual positions of all circles
    const circlePositions = circleRefs.map((ref) => {
      const rect = ref.current!.getBoundingClientRect();
      return rect.left + rect.width / 2 - containerRect.left;
    });

    // Sort positions to get leftmost and rightmost regardless of array order
    const sortedPositions = [...circlePositions].sort((a, b) => a - b);
    const leftMostPosition = sortedPositions[0];
    const rightMostPosition = sortedPositions[sortedPositions.length - 1];
    const totalWidth = rightMostPosition - leftMostPosition;

    // Find current section index
    const currentSectionIndex = uniqueLabels.findIndex((label) => {
      const sectionSteps = steps.filter((step) => step.label === label);
      const firstIndex = steps.indexOf(sectionSteps[0]);
      const lastIndex = steps.indexOf(sectionSteps[sectionSteps.length - 1]);
      return currentStep >= firstIndex && currentStep <= lastIndex;
    });

    if (currentSectionIndex === -1) {
      setLineDimensions({
        progressWidth: 0,
        totalWidth,
        lineLeft: leftMostPosition,
        progressLeft: isRTL ? rightMostPosition : leftMostPosition,
      });
      return;
    }

    // Get the position of the first step (index 0) and current step
    const firstStepPosition = circlePositions[0];
    const currentStepPosition = circlePositions[currentSectionIndex];

    let progressWidth = 0;
    let progressLeft = 0;

    // Calculate progress based on visual positions
    progressWidth = Math.abs(currentStepPosition - firstStepPosition);

    // Add partial progress within current section
    if (currentSectionIndex < circlePositions.length - 1) {
      const currentLabel = uniqueLabels[currentSectionIndex];
      const sectionSteps = steps.filter((step) => step.label === currentLabel);
      const currentStepInSection = currentStep - steps.indexOf(sectionSteps[0]);

      const nextStepPosition = circlePositions[currentSectionIndex + 1];
      const sectionWidth = Math.abs(nextStepPosition - currentStepPosition);
      const partialProgress = (currentStepInSection / sectionSteps.length) * sectionWidth;
      progressWidth += partialProgress;
    }

    // Determine progress bar starting position
    if (isRTL) {
      // In RTL, first step is visually on the right, progress grows to the left
      progressLeft = firstStepPosition - progressWidth;
    } else {
      // In LTR, first step is visually on the left, progress grows to the right
      progressLeft = firstStepPosition;
    }

    setLineDimensions({
      progressWidth: Math.max(0, progressWidth),
      totalWidth,
      lineLeft: leftMostPosition,
      progressLeft,
    });
  };

  const scrollToActiveStep = () => {
    if (!containerRef.current) return;

    const currentStepIndex = uniqueLabels.findIndex((label) => {
      const sectionSteps = steps.filter((step) => step.label === label);
      const firstIndex = steps.indexOf(sectionSteps[0]);
      const lastIndex = steps.indexOf(sectionSteps[sectionSteps.length - 1]);
      return currentStep >= firstIndex && currentStep <= lastIndex;
    });

    if (currentStepIndex === -1 || !circleRefs[currentStepIndex]?.current) return;

    const activeStepElement = circleRefs[currentStepIndex].current;

    activeStepElement.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateDimensions();
    }, 100);

    const handleResize = () => {
      requestAnimationFrame(calculateDimensions);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [currentStep, steps, uniqueLabels, isRTL]);

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToActiveStep();
    }, 150);

    return () => clearTimeout(timer);
  }, [currentStep]);

  return (
    <div
      ref={containerRef}
      className={`no-scrollbar relative mt-8 w-full overflow-x-auto bg-white py-6 ${indicatorWrapperClassName || ""}`}
    >
      <div
        ref={innerContainerRef}
        className={`min-w-[400px] md:min-w-[600px] md:max-w-full ${containerClassName || ""}`}
      >
        <div className="relative flex justify-between">
          {/* Background line */}
          <div
            className="absolute top-[3px] h-[2px] bg-gray-200"
            style={{
              left: lineDimensions.lineLeft,
              width: lineDimensions.totalWidth,
              zIndex: 1,
            }}
          />
          {/* Progress line */}
          <div
            className="absolute top-[3px] h-[2px] bg-red-500 transition-all duration-300"
            style={{
              left: lineDimensions.progressLeft,
              width: lineDimensions.progressWidth,
              zIndex: 2,
            }}
          />

          {uniqueLabels.map((label, index) => {
            const isActive = currentStep >= steps.findIndex((step) => step.label === label);

            return (
              <div key={`${label}-${index}`} className="flex flex-col items-center">
                <div className="relative">
                  <div
                    ref={circleRefs[index]}
                    className={`relative z-[3] h-[8px] w-[8px] rounded-full ${isActive ? "bg-red-500" : "bg-gray-300"}`}
                  />
                  {/* Larger circle outline */}
                  <div
                    className={`absolute left-[-2px] top-[-2px] z-[3] h-[12px] w-[12px] rounded-full border-4 ${isActive ? "border-red-500" : "border-gray-300"}`}
                  />
                </div>
                <span
                  className={`inter mt-3 max-w-[70px] text-center text-sm font-medium leading-tight md:max-w-none ${
                    isActive ? "text-red-500" : "text-gray-400"
                  }`}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepIndicator;
